/**
 * Auth Slice - Authentication State Management
 * Enterprise-grade authentication state with Redux Toolkit
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthApiClient, LoginRequest, RegisterRequest, LoginResponse, User } from '@/infrastructure/api';
import type { RootState } from '@/shared/providers/store';

// Types
export interface AuthState {
  // User state
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Login/Register state
  loginError: string | null;
  registerError: string | null;

  // MFA state
  requiresMfa: boolean;
  mfaUserId: string | null;
  mfaMethods: string[];
  mfaError: string | null;

  // Session state
  sessionToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;

  // Profile loading
  profileLoading: boolean;
  profileError: string | null;

  // Password change
  passwordChangeLoading: boolean;
  passwordChangeError: string | null;

  // MFA setup
  mfaSetupLoading: boolean;
  mfaSetupError: string | null;
  mfaSetupData: any | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,

  loginError: null,
  registerError: null,

  requiresMfa: false,
  mfaUserId: null,
  mfaMethods: [],
  mfaError: null,

  sessionToken: null,
  accessToken: null,
  refreshToken: null,

  profileLoading: false,
  profileError: null,

  passwordChangeLoading: false,
  passwordChangeError: null,

  mfaSetupLoading: false,
  mfaSetupError: null,
  mfaSetupData: null,
};

// Create auth client instance
const authClient = new AuthApiClient();

// Async thunks
export const loginAsync = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authClient.login(credentials);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Login failed');
    }
  }
);

export const registerAsync = createAsyncThunk(
  'auth/register',
  async (userData: RegisterRequest, { rejectWithValue }) => {
    try {
      const response = await authClient.register(userData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Registration failed');
    }
  }
);

export const verifyMfaAsync = createAsyncThunk(
  'auth/verifyMfa',
  async (data: { mfa_user_id: string; code: string; method?: string }, { rejectWithValue }) => {
    try {
      const response = await authClient.verifyMFA({
        mfa_user_id: data.mfa_user_id,
        code: data.code,
        method: (data.method ?? 'totp') as 'totp' | 'webauthn' | 'sms' | 'email'
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'MFA verification failed');
    }
  }
);

export const logoutAsync = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authClient.logout();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Logout failed');
    }
  }
);

export const getProfileAsync = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authClient.getProfile();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load profile');
    }
  }
);

export const changePasswordAsync = createAsyncThunk(
  'auth/changePassword',
  async (data: { current_password: string; new_password: string }, { rejectWithValue }) => {
    try {
      const response = await authClient.changePassword(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Password change failed');
    }
  }
);

export const setupMfaAsync = createAsyncThunk(
  'auth/setupMfa',
  async (method: 'totp' | 'webauthn' = 'totp', { rejectWithValue }) => {
    try {
      const response = await authClient.setupMFA(method);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'MFA setup failed');
    }
  }
);

export const refreshTokenAsync = createAsyncThunk(
  'auth/refreshToken',
  async (refreshToken: string | undefined, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const token = refreshToken ?? state.auth.refreshToken ?? '';
      const response = await authClient.refreshToken(token);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Token refresh failed');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Synchronous actions
    clearErrors: (state) => {
      state.loginError = null;
      state.registerError = null;
      state.mfaError = null;
      state.profileError = null;
      state.passwordChangeError = null;
      state.mfaSetupError = null;
    },

    clearMfaState: (state) => {
      state.requiresMfa = false;
      state.mfaUserId = null;
      state.mfaMethods = [];
      state.mfaError = null;
    },

    setAuthTokens: (state, action: PayloadAction<{
      sessionToken?: string;
      accessToken?: string;
      refreshToken?: string;
    }>) => {
      if (action.payload.sessionToken) {
        state.sessionToken = action.payload.sessionToken;
        // Store in localStorage for persistence
        if (typeof window !== 'undefined') {
          // LAUNCH GATE: no tokens in browser
        }
      }
      if (action.payload.accessToken) {
        state.accessToken = action.payload.accessToken;
      }
      if (action.payload.refreshToken) {
        state.refreshToken = action.payload.refreshToken;
      }
    },

    clearAuthTokens: (state) => {
      state.sessionToken = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.isAuthenticated = false;

      // Clear from localStorage
      if (typeof window !== 'undefined') {
        if (typeof window !== 'undefined') try { localStorage.removeItem('session_token'); } catch { /* no-op */ }
      }
    },

    // Initialize auth state from localStorage
    initializeAuth: (state) => {
      if (typeof window !== 'undefined') {
        const sessionToken = localStorage.getItem('session_token');
        if (sessionToken) {
          state.sessionToken = sessionToken;
          // Do not mark authenticated until profile validation succeeds
          state.isAuthenticated = false;
        }
      }
    },
  },

  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginAsync.pending, (state) => {
        state.isLoading = true;
        state.loginError = null;
      })
      .addCase(loginAsync.fulfilled, (state, action: PayloadAction<LoginResponse>) => {
        state.isLoading = false;

        if (action.payload.requires_mfa) {
          state.requiresMfa = true;
          state.mfaUserId = action.payload.mfa_user_id || null;
          state.mfaMethods = action.payload.mfa_methods || [];
        } else if (action.payload.success) {
          state.isAuthenticated = true;
          state.user = action.payload.user || null;
          // NOTE: Tokens are stored in httpOnly cookies by the server
          // We do NOT store them in localStorage for security reasons (XSS protection)
          state.sessionToken = null; // Not stored client-side
          state.accessToken = null;
          state.refreshToken = null;
          state.requiresMfa = false;

          // Store only non-sensitive user metadata for UI purposes
          if (typeof window !== 'undefined' && action.payload.user) {
            const userType = action.payload.user.userType;
            // LAUNCH GATE: no auth state in browser storage
          }
        }
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.loginError = action.payload as string;
      });

    // Register
    builder
      .addCase(registerAsync.pending, (state) => {
        state.isLoading = true;
        state.registerError = null;
      })
      .addCase(registerAsync.fulfilled, (state, action: PayloadAction<User>) => {
        state.isLoading = false;
        state.isAuthenticated = !!action.payload;
        state.user = action.payload || null;
      })
      .addCase(registerAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.registerError = action.payload as string;
      });

    // MFA Verify
    builder
      .addCase(verifyMfaAsync.pending, (state) => {
        state.isLoading = true;
        state.mfaError = null;
      })
      .addCase(verifyMfaAsync.fulfilled, (state, action: PayloadAction<LoginResponse>) => {
        state.isLoading = false;
        if (action.payload.success) {
          state.isAuthenticated = true;
          state.user = action.payload.user || null;
          state.sessionToken = action.payload.session_token || null;
          state.accessToken = action.payload.access_token || null;
          state.requiresMfa = false;
          state.mfaUserId = null;
          state.mfaMethods = [];
        }
      })
      .addCase(verifyMfaAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.mfaError = action.payload as string;
      });

    // Logout
    builder
      .addCase(logoutAsync.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.sessionToken = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.requiresMfa = false;
        state.mfaUserId = null;
        state.mfaMethods = [];
      });

    // Get Profile
    builder
      .addCase(getProfileAsync.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(getProfileAsync.fulfilled, (state, action: PayloadAction<User>) => {
        state.profileLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getProfileAsync.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload as string;
        state.isAuthenticated = false;
        state.sessionToken = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.user = null;
      });

    // Change Password
    builder
      .addCase(changePasswordAsync.pending, (state) => {
        state.passwordChangeLoading = true;
        state.passwordChangeError = null;
      })
      .addCase(changePasswordAsync.fulfilled, (state) => {
        state.passwordChangeLoading = false;
      })
      .addCase(changePasswordAsync.rejected, (state, action) => {
        state.passwordChangeLoading = false;
        state.passwordChangeError = action.payload as string;
      });

    // Setup MFA
    builder
      .addCase(setupMfaAsync.pending, (state) => {
        state.mfaSetupLoading = true;
        state.mfaSetupError = null;
      })
      .addCase(setupMfaAsync.fulfilled, (state, action) => {
        state.mfaSetupLoading = false;
        state.mfaSetupData = action.payload;
      })
      .addCase(setupMfaAsync.rejected, (state, action) => {
        state.mfaSetupLoading = false;
        state.mfaSetupError = action.payload as string;
      });
  },
});

// Export actions
export const {
  clearErrors,
  clearMfaState,
  setAuthTokens,
  clearAuthTokens,
  initializeAuth,
} = authSlice.actions;

// Export reducer
export default authSlice.reducer;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectRequiresMfa = (state: { auth: AuthState }) => state.auth.requiresMfa;
export const selectMfaMethods = (state: { auth: AuthState }) => state.auth.mfaMethods;
